import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeChat, sendMessage, clearHistory, newConversation } from '../../redux/AIChatSlice/aiChatSlice';
import { setScrollTargetJobId } from '../../redux/JobSlice/jobSlice';
import { X, Send, Maximize2, Trash2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AIAssistantJobModal from './AIAssistantJobModal';

const TypingText = ({ text, isAssistant, animateTyping = false, messageKey, onTypingComplete }) => {
  const [displayText, setDisplayText] = useState(text || '');

  useEffect(() => {
    if (!isAssistant) {
      setDisplayText(text || '');
      return;
    }

    const fullText = text || '';
    if (!fullText) {
      setDisplayText('');
      return;
    }

    if (!animateTyping) {
      setDisplayText(fullText);
      return;
    }

    let index = 0;
    setDisplayText('');
    const tickMs = 16;
    const targetDurationMs = Math.max(320, Math.min(1400, fullText.length * 10));
    const totalTicks = Math.max(1, Math.round(targetDurationMs / tickMs));
    const charsPerTick = Math.max(1, Math.ceil(fullText.length / totalTicks));

    const timer = setInterval(() => {
      index = Math.min(fullText.length, index + charsPerTick);
      setDisplayText(fullText.slice(0, index));
      if (index >= fullText.length) {
        clearInterval(timer);
        onTypingComplete?.(messageKey);
      }
    }, tickMs);

    return () => clearInterval(timer);
  }, [text, isAssistant, animateTyping, onTypingComplete, messageKey]);

  return <p className="font-dmsans text-sm whitespace-pre-wrap">{displayText}</p>;
};

const AIAssistantSidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const { isOpen, messages, isLoading, currentJobs, followUpQuestion } = useSelector((state) => state.aiChat);
  const user = useSelector((state) => state.auth.user);
  
  const [input, setInput] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [typedMessageKeys, setTypedMessageKeys] = useState(new Set());
  const initializedTypingRef = useRef(false);

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const getMessageKey = (msg, idx) => {
    const ts = msg?.timestamp || 'no-ts';
    const contentLen = (msg?.content || '').length;
    return `${msg?.role || 'unknown'}-${ts}-${contentLen}-${idx}`;
  };

  useEffect(() => {
    if (initializedTypingRef.current) return;
    const existingKeys = new Set(
      messages
        .map((msg, idx) => ({ msg, idx }))
        .filter(({ msg }) => msg?.role === 'assistant')
        .map(({ msg, idx }) => getMessageKey(msg, idx))
    );
    setTypedMessageKeys(existingKeys);
    initializedTypingRef.current = true;
  }, [messages]);

  const markTyped = (key) => {
    if (!key) return;
    setTypedMessageKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const handleSend = (messageText = null) => {
    const textToSend = messageText || input;
    if (textToSend.trim() && !isLoading) {
      dispatch(sendMessage(textToSend));
      setInput('');
    }
  };

  const handleCompanySuggestionClick = (companyName, action = 'analyze_hiring_signals') => {
    if (!companyName || isLoading) return;
    const msg =
      action === 'find_recruiters'
        ? `Find recruiters at ${companyName}`
        : `Analyze hiring signals for ${companyName}`;
    dispatch(sendMessage(msg));
  };

  const handleChatAction = (action, context = {}) => {
    if (isLoading) return;

    switch (action) {
      case 'draft_email': {
        const companyName = context.company;
        if (companyName) {
          const msg = `Draft an email to reach out to recruiters at ${companyName}`;
          dispatch(sendMessage(msg));
        }
        break;
      }

      case 'hiring_signals': {
        const company = context.company;
        if (company) {
          const msg = `Show hiring signals for ${company}`;
          dispatch(sendMessage(msg));
        }
        break;
      }

      case 'find_similar': {
        const targetCompany = context.company;
        if (targetCompany) {
          const msg = `Find companies similar to ${targetCompany} that are hiring`;
          dispatch(sendMessage(msg));
        }
        break;
      }

      case 'find_recruiters': {
        const companyForRecruiters = context.company;
        if (companyForRecruiters) {
          const msg = `Find recruiters at ${companyForRecruiters}`;
          dispatch(sendMessage(msg));
        }
        break;
      }

      case 'copy_email': {
        const email = context.email;
        if (!email) return;
        const textToCopy = `Subject: ${email.subject}\n\n${email.body}`;
        if (navigator?.clipboard?.writeText) {
          navigator.clipboard.writeText(textToCopy).then(() => {
            window.alert('Email copied to clipboard');
          }).catch(() => {
            const ta = document.createElement('textarea');
            ta.value = textToCopy;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            window.alert('Email copied to clipboard');
          });
        } else {
          const ta = document.createElement('textarea');
          ta.value = textToCopy;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          window.alert('Email copied to clipboard');
        }
        break;
      }

      case 'open_in_email': {
        const email = context.email;
        const to = (context.recruiter && context.recruiter.email) ? context.recruiter.email : '';
        if (!email) return;
        const subject = encodeURIComponent(email.subject || '');
        const body = encodeURIComponent(email.body || '');
        const mailto = `mailto:${to}?subject=${subject}&body=${body}`;
        window.location.href = mailto;
        break;
      }

      case 'refine_email': {
        const email = context.email;
        if (!email) return;
        const tone = context.tone || 'more concise and professional';
        const prompt = `Refine this outreach email to be ${tone}:\n\nSubject: ${email.subject}\n\n${email.body}`;
        dispatch(sendMessage(prompt));
        break;
      }

      case 'find_other_recruiters':
      case 'show_other_recruiters': {
        const c = context.company;
        if (c) dispatch(sendMessage(`Find more recruiters at ${c}`));
        break;
      }

      default:
        break;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleJobClick = (job) => {
    const targetJobId = job?._id || job?.id || null;
    if (targetJobId) {
      dispatch(setScrollTargetJobId(String(targetJobId)));
    }
    navigate('/', { state: { chatTargetJob: job } });
    dispatch(closeChat());
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      dispatch(clearHistory());
    }
  };

  const handleNewConversation = () => {
    dispatch(newConversation());
    setTypedMessageKeys(new Set());
    initializedTypingRef.current = true;
    setInput('');
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-2xl z-[100] flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#3F57E0] to-[#7B61FF] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
            CC
          </div>
          <div>
            <h3 className="font-dmsans font-semibold text-base">Career Champ</h3>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span>Online</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewConversation}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="New conversation"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={handleClearHistory}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Clear chat history"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/ai-assistant')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Open in full page"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => dispatch(closeChat())}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <div className="w-16 h-16 bg-gradient-to-r from-[#3F57E0] to-[#7B61FF] rounded-full flex items-center justify-center mb-4">
              <span className="text-white font-bold text-2xl">AI</span>
            </div>
            <h4 className="font-dmsans font-semibold text-lg text-gray-800 mb-2">
              Welcome to CareerChamp AI
            </h4>
            <p className="font-dmsans text-sm text-gray-600 mb-6">
              I'm here to help you find the perfect job. Ask me about jobs, companies, or your career.
            </p>
            
            {/* Example prompts */}
            <div className="w-full space-y-2">
              <button
                onClick={() => handleSend('Find hidden jobs that are not on LinkedIn')}
                className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-[#3F57E0] hover:bg-blue-50 transition-colors text-sm font-dmsans"
              >
                Find Hidden Jobs
              </button>
              <button
                onClick={() => handleSend('Show me best referral opportunities')}
                className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-[#3F57E0] hover:bg-blue-50 transition-colors text-sm font-dmsans"
              >
                Find Best Referral Opportunities
              </button>
              <button
                onClick={() => handleSend('Help me follow up on my job search steps')}
                className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-[#3F57E0] hover:bg-blue-50 transition-colors text-sm font-dmsans"
              >
                Help me follow up on my job search steps
              </button>
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg, idx) => {
              const messageJobs = msg.metadata?.jobs || msg.jobs || [];
              const messageCompanies = msg.metadata?.companies || [];
              const contextUsed = msg.metadata?.contextUsed || null;
              const showContextRow = Boolean(msg.metadata?.contextFallback || contextUsed);
              const suggestionAction = msg.metadata?.suggestionAction || 'analyze_hiring_signals';
              return (
              <div
                key={idx}
                className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-[#3F57E0] text-white'
                      : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  <TypingText
                    text={msg.content}
                    isAssistant={msg.role === 'assistant'}
                    messageKey={getMessageKey(msg, idx)}
                    animateTyping={
                      msg.role === 'assistant' &&
                      idx === messages.length - 1 &&
                      !typedMessageKeys.has(getMessageKey(msg, idx))
                    }
                    onTypingComplete={markTyped}
                  />
                  {msg.role === 'assistant' && messageCompanies.length > 0 && (
                    <div className="mt-3">
                      {showContextRow && (
                        <div className="mb-2 flex flex-wrap items-center gap-1.5">
                          <span className="text-[11px] text-gray-500 mr-1">Using your context</span>
                          {contextUsed?.industry && (
                            <span className="rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                              {contextUsed.industry}
                            </span>
                          )}
                          {contextUsed?.department && (
                            <span className="rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700">
                              {contextUsed.department}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        {messageCompanies.slice(0, 6).map((c, jobIdx) => {
                          const name = c?.company || c?.name || c;
                          return (
                            <button
                              key={`${name}-${jobIdx}`}
                              type="button"
                              onClick={() => handleCompanySuggestionClick(name, suggestionAction)}
                              className="rounded-full bg-white border border-gray-200 px-3 py-1.5 text-[12px] font-semibold text-[#3F57E0] hover:bg-blue-50 hover:border-[#3F57E0] transition-colors"
                            >
                              {name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {msg.role === 'assistant' && msg.metadata?.nextSteps && msg.metadata.nextSteps.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {msg.metadata.nextSteps.map((step, stepIdx) => (
                        <button
                          key={stepIdx}
                          type="button"
                          onClick={() => handleChatAction(step.action, {
                            company: msg.metadata?.company || msg.metadata?.originalIntent?.company || (messageCompanies[0] && (messageCompanies[0].company || messageCompanies[0].name || messageCompanies[0])),
                            email: msg.metadata?.email,
                            recruiter: msg.metadata?.recruiter,
                            tone: step.tone || null
                          })}
                          className="rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 border border-[#3F57E0] px-3 py-1.5 text-[11px] font-semibold text-[#3F57E0] hover:from-blue-100 hover:to-purple-100 transition-colors"
                        >
                          {step.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {messageJobs.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {messageJobs.map((job, jobIdx) => (
                        <div
                          key={jobIdx}
                          onClick={() => handleJobClick(job)}
                          className="bg-white p-3.5 rounded-xl border border-gray-200 hover:border-[#3F57E0] hover:shadow-sm cursor-pointer transition-all"
                        >
                          <p className="font-dmsans font-semibold text-[13.5px] text-gray-900 leading-snug">
                            {job.title || job.jobPosition}
                          </p>
                          <p className="font-dmsans text-[12px] text-gray-600 mt-0.5">{job.company || job.companyName}</p>
                          {job.location && (
                            <p className="font-dmsans text-[12px] text-gray-500 mt-1">
                              {job.location || job.jobLocation}
                            </p>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <span className="text-[11px] text-gray-400">Open in Jobs</span>
                            <span className="text-[13px] font-semibold text-[#3F57E0]">View</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
            
            {/* Follow-up Question */}
            {!isLoading && followUpQuestion && (
              <div className="mb-4 flex justify-start">
                <div className="max-w-[85%] bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-[#3F57E0] rounded-lg p-3">
                  <p className="font-dmsans text-xs text-gray-600 mb-2">💡 Suggested next step:</p>
                  <button
                    onClick={() => handleSend(followUpQuestion)}
                    className="w-full text-left font-dmsans text-sm text-[#3F57E0] font-medium hover:underline"
                  >
                    {followUpQuestion}
                  </button>
                </div>
              </div>
            )}
            
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-[#3F57E0]">
                    <span className="text-sm font-dmsans">Typing</span>
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-[#3F57E0] rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-[#3F57E0] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-1.5 h-1.5 bg-[#3F57E0] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-200">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about jobs, companies, or anything..."
            className="flex-1 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#3F57E0] focus:border-transparent font-dmsans text-sm"
            rows="2"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-3 bg-[#3F57E0] text-white rounded-lg hover:bg-[#3548C7] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            title="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
    <AIAssistantJobModal
      isOpen={isJobModalOpen}
      onClose={() => {
        setIsJobModalOpen(false);
        setSelectedJob(null);
      }}
      job={selectedJob}
    />
    </>
  );
};

export default AIAssistantSidebar;
